import { rgba, math } from 'polished';
import { css } from 'styled-components';
import { SDS_COLOR_BLACK } from '@s-group/design-system-tokens/web/tokens/colors.es6.js';
import { SDS_MISC_ALPHA_SMALL } from '@s-group/design-system-tokens/web/tokens/misc.es6.js';
import { SDS_SHADOW_ELEVATION_HIGH_Y, SDS_SHADOW_ELEVATION_HIGH_BLUR } from '@s-group/design-system-tokens/web/tokens/shadow.es6.js';
import { SDS_SIZE_BORDER_RADIUS_XSMALL, SDS_SIZE_BORDER_SMALL } from '@s-group/design-system-tokens/web/tokens/size.es6.js';
import { SDS_SPACE_XXSMALL, SDS_SPACE_XSMALL, SDS_SPACE_MEDIUM, SDS_SPACE_SMALL } from '@s-group/design-system-tokens/web/tokens/space.es6.js';
import { SDS_ZINDEX_MODAL, SDS_ZINDEX_BLANKET } from '@s-group/design-system-tokens/web/tokens/zindex.es6.js';
import { SDS_TEXT_BODY_SMALL_REGULAR, SDS_TEXT_HEADING_XXSMALL_MEDIUM, SDS_TEXT_HEADING_XSMALL_MEDIUM, SDS_TEXT_HEADING_SMALL_MEDIUM } from '@s-group/design-system-tokens/web/tokens/text.es6.js';
import { bordered, scaledIcon } from '../common/util.js';
import 'react';
import { SDS } from '../common/namespace.js';
import { fontMetrics, paddingMetrics } from '../Button/Button.styles.js';
import { buttonSizeOptions } from './ModalHeader.js';

const blanketColor = ({ blanket }) => blanket ? rgba(SDS_COLOR_BLACK, SDS_MISC_ALPHA_SMALL) : 'transparent';
const blanketPadding = ({ cover }) => cover ? 0 : SDS_SPACE_MEDIUM;
const borderRadius = ({ cover, rounding }) => !cover && rounding === 'rounded' && SDS_SIZE_BORDER_RADIUS_XSMALL;
function spacing({ sizing }) {
    if (sizing) {
        return {
            small: SDS_SPACE_XSMALL,
            medium: SDS_SPACE_SMALL,
            large: SDS_SPACE_MEDIUM,
        }[sizing];
    }
}
const gap = ({ divider, sizing }) => divider ? 0 : spacing({ sizing });
const bodySpacing = ({ divider, sizing }) => divider ? spacing({ sizing }) : undefined;
const dividerBorder = ({ divider, theme }) => divider
    ? `${SDS_SIZE_BORDER_SMALL} solid ${theme[SDS].colors.SDS_BRAND_COLOR_BORDER_WEAK_NEUTRAL}`
    : undefined;
const dividerSpacing = ({ divider, sizing }) => divider && sizing ? bordered(spacing({ sizing })) : undefined;
const inset = ({ cover }) => (cover ? 0 : undefined);
const margin = ({ cover, position }) => cover
    ? 0
    : position &&
        {
            top: '0 auto auto',
            center: 'auto',
            bottom: 'auto auto 0',
        }[position];
const maxWidth = ({ cover, width }) => !cover &&
    width &&
    {
        small: '20rem',
        medium: '30rem',
        large: '40rem',
    }[width];
function headingText({ sizing }) {
    return (sizing &&
        {
            small: SDS_TEXT_HEADING_XXSMALL_MEDIUM,
            medium: SDS_TEXT_HEADING_XSMALL_MEDIUM,
            large: SDS_TEXT_HEADING_SMALL_MEDIUM,
        }[sizing]);
}
// Icon is scaled in relation to the header font size.
const iconSize = ({ sizing }) => {
    if (sizing) {
        return scaledIcon(headingText({ sizing })).iconSize;
    }
};
const iconMargin = ({ sizing }) => {
    if (sizing) {
        return scaledIcon(headingText({ sizing })).iconMargin;
    }
};
const closeMargin = ({ sizing }) => {
    var _a;
    if (!sizing)
        return;
    const [, buttonLineHeight] = fontMetrics[buttonSizeOptions[sizing]];
    const [buttonVerticalPadding] = paddingMetrics[buttonSizeOptions[sizing]];
    const headingLineHeight = (_a = headingText({ sizing })) === null || _a === void 0 ? void 0 : _a.lineHeight;
    const lineHeightCompensation = math(`(${headingLineHeight} - ${buttonLineHeight}) / 2`);
    const spacing = {
        small: SDS_SPACE_XXSMALL,
        medium: SDS_SPACE_XXSMALL,
        large: SDS_SPACE_XSMALL,
    }[sizing];
    const horizontalMargin = math(`-1 * ${spacing}`);
    const verticalMargin = math(`-1 * (${buttonVerticalPadding} - ${lineHeightCompensation})`);
    return `${verticalMargin} ${horizontalMargin}`;
};
const ModalStyle = css `
    ${SDS_TEXT_BODY_SMALL_REGULAR};
    background-color: ${({ theme }) => theme[SDS].colors.SDS_BRAND_COLOR_ELEVATION_HIGH};
    border-radius: ${borderRadius};
    box-shadow: 0 ${SDS_SHADOW_ELEVATION_HIGH_Y}
        ${SDS_SHADOW_ELEVATION_HIGH_BLUR} 0 ${rgba(SDS_COLOR_BLACK, 0.16)};
    box-sizing: border-box;
    color: ${({ theme }) => theme[SDS].colors.SDS_BRAND_COLOR_TEXT_DEFAULT_NEUTRAL};
    display: flex;
    flex: 1 1 100%;
    flex-direction: column;
    gap: ${gap};
    inset: ${inset};
    margin: ${margin};
    max-height: 100%;
    max-width: ${maxWidth};
    position: relative;
    z-index: ${SDS_ZINDEX_MODAL};

    > :first-child {
        padding-top: ${spacing};
    }

    > * {
        padding-left: ${spacing};
        padding-right: ${spacing};
    }

    .modal-header {
        ${headingText};
        border-bottom: ${dividerBorder};
        display: flex;
        justify-content: space-between;
        padding-bottom: ${dividerSpacing};

        .modal-label {
            margin-right: auto;

            &:not(:first-child) {
                padding-left: ${SDS_SPACE_XXSMALL};
            }

            &:not(:last-child) {
                padding-right: ${SDS_SPACE_XSMALL};
            }
        }

        > h2,
        > h3,
        > h4,
        > h5,
        > h6 {
            color: inherit;
            font: inherit;
            margin: inherit;
        }
    }

    .modal-icon {
        color: ${({ theme }) => theme[SDS].colors.SDS_BRAND_COLOR_ELEMENT_DEFAULT_NEUTRAL};
        height: ${iconSize};
        margin: ${iconMargin};
        width: ${iconSize};

        svg {
            height: auto;
            width: auto;
        }
    }

    .modal-close {
        align-self: start;
        line-height: 0; /* Only icon height should determine size */
        margin: ${closeMargin};
    }

    .modal-body {
        flex: 1;
        overflow: auto;
        padding-bottom: ${bodySpacing};
        padding-top: ${bodySpacing};
    }

    .modal-footer {
        border-top: ${dividerBorder};
        padding-top: ${dividerSpacing};
    }

    > :last-child {
        padding-bottom: ${spacing};
    }
`;
const BlanketStyle = css `
    background-color: ${blanketColor};
    box-sizing: border-box;
    display: flex;
    height: 100%;
    inset: 0;
    padding: ${blanketPadding};
    position: fixed;
    z-index: ${SDS_ZINDEX_BLANKET};
`;
const FocusTrapStyle = css `
    /* Visually hidden, accessible for screen readers */
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    height: 1px;
    overflow: hidden;
    position: absolute;
    white-space: nowrap;
    width: 1px;
`;

export { BlanketStyle, FocusTrapStyle, ModalStyle };
//# sourceMappingURL=Modal.styles.js.map
